/**
 * Assisted Booking Agent Management Page
 * Allows admins to create and manage staff accounts for the Assisted Booking portal
 */
import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

const AssistedBookingAgents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [showPin, setShowPin] = useState(false);
  
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: ""
  });
  const [createLoading, setCreateLoading] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/assisted-booking-agents/list");
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get("/assisted-booking-agents/stats/summary");
      setStats(response.data.stats);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchStats();
  }, [fetchAgents, fetchStats]);

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email) {
      toast.error("Name and email are required");
      return;
    }
    try {
      setCreateLoading(true);
      const response = await api.post("/assisted-booking-agents/create", createForm);
      setNewCredentials({
        email: response.data.email,
        pin: response.data.pin,
        password: response.data.temp_password,
        agent_id: response.data.agent_id,
        name: response.data.name
      });
      setShowCreateModal(false);
      setShowCredentialsModal(true);
      setCreateForm({ name: "", email: "", phone: "", notes: "" });
      fetchAgents();
      fetchStats();
      toast.success("Agent account created!");
    } catch (error) {
      console.error("Failed to create agent:", error);
      toast.error(error.response?.data?.error?.message || error.response?.data?.detail || "Failed to create agent");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleResetPin = async (agentId, agentEmail) => {
    if (!window.confirm(`Reset PIN for ${agentEmail}?`)) return;
    try {
      const response = await api.post(`/assisted-booking-agents/${agentId}/reset-pin`);
      setNewCredentials({
        email: response.data.agent_email,
        pin: response.data.new_pin,
        agent_id: agentId
      });
      setShowCredentialsModal(true);
      toast.success("PIN reset successfully");
    } catch (error) {
      console.error("Failed to reset PIN:", error);
      toast.error("Failed to reset PIN");
    }
  };

  const handleToggleActive = async (agentId, currentStatus) => {
    try {
      await api.put(`/assisted-booking-agents/${agentId}`, { is_active: !currentStatus });
      fetchAgents();
      toast.success(currentStatus ? "Agent deactivated" : "Agent activated");
    } catch (error) {
      console.error("Failed to update agent:", error);
      toast.error("Failed to update agent");
    }
  };

  const handleDeactivate = async (agentId, agentEmail) => {
    if (!window.confirm(`Permanently deactivate ${agentEmail}? This cannot be undone.`)) return;
    try {
      await api.delete(`/assisted-booking-agents/${agentId}`);
      fetchAgents();
      fetchStats();
      toast.success("Agent deactivated");
    } catch (error) {
      console.error("Failed to deactivate agent:", error);
      toast.error("Failed to deactivate agent");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="assisted-booking-agents-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <svg className="h-7 w-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              Assisted Booking Agents
            </h1>
            <p className="text-slate-400 mt-1">Manage staff accounts for the booking portal (PIN-based access)</p>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-all" data-testid="add-agent-btn">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Add Agent
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800 rounded-xl p-4"><p className="text-slate-400 text-sm">Total Agents</p><p className="text-2xl font-bold text-white">{stats.total_agents}</p></div>
            <div className="bg-slate-800 rounded-xl p-4"><p className="text-slate-400 text-sm">Active Agents</p><p className="text-2xl font-bold text-emerald-400">{stats.active_agents}</p></div>
            <div className="bg-slate-800 rounded-xl p-4"><p className="text-slate-400 text-sm">Total Bookings</p><p className="text-2xl font-bold text-white">{stats.total_assisted_bookings}</p></div>
            <div className="bg-slate-800 rounded-xl p-4"><p className="text-slate-400 text-sm">Conversion Rate</p><p className="text-2xl font-bold text-amber-400">{stats.conversion_rate}%</p></div>
          </div>
        )}

        <div className="bg-slate-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700"><h2 className="text-lg font-semibold text-white">All Agents</h2></div>
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading agents...</div>
          ) : agents.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="h-12 w-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /></svg>
              <p className="text-slate-400">No agents created yet</p>
              <button onClick={() => setShowCreateModal(true)} className="mt-4 text-amber-500 hover:text-amber-400">Create your first agent</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-slate-400 font-medium">Agent</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Contact</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Bookings</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left p-4 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr key={agent.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                          </div>
                          <div><p className="text-white font-medium">{agent.name}</p><p className="text-slate-400 text-sm">{agent.id}</p></div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2 text-slate-300"><svg className="h-4 w-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>{agent.email}</div>
                        {agent.phone && <div className="flex items-center gap-2 text-slate-400 text-sm mt-1"><svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>{agent.phone}</div>}
                      </td>
                      <td className="p-4"><span className="text-white font-semibold">{agent.bookings_created || 0}</span><span className="text-slate-400 text-sm ml-1">created</span></td>
                      <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${agent.is_active ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}`}>{agent.is_active ? "Active" : "Inactive"}</span></td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleResetPin(agent.id, agent.email)} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-amber-400" title="Reset PIN"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg></button>
                          <button onClick={() => handleToggleActive(agent.id, agent.is_active)} className={`p-2 rounded-lg ${agent.is_active ? "bg-slate-700 hover:bg-red-500/20 text-slate-400 hover:text-red-400" : "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"}`} title={agent.is_active ? "Deactivate" : "Activate"}>{agent.is_active ? <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}</button>
                          <button onClick={() => handleDeactivate(agent.id, agent.email)} className="p-2 rounded-lg bg-slate-700 hover:bg-red-500/20 text-slate-400 hover:text-red-400" title="Delete Permanently"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Create New Agent</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
              <form onSubmit={handleCreateAgent} className="p-4 space-y-4">
                <div><label className="block text-slate-400 text-sm mb-1">Full Name *</label><input type="text" value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500" placeholder="Karishma Brown" required /></div>
                <div><label className="block text-slate-400 text-sm mb-1">Email Address *</label><input type="email" value={createForm.email} onChange={(e) => setCreateForm({...createForm, email: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500" placeholder="karishma@liantiaoja.com" required /></div>
                <div><label className="block text-slate-400 text-sm mb-1">Phone (Optional)</label><input type="tel" value={createForm.phone} onChange={(e) => setCreateForm({...createForm, phone: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500" placeholder="876-555-1234" /></div>
                <div><label className="block text-slate-400 text-sm mb-1">Notes (Optional)</label><textarea value={createForm.notes} onChange={(e) => setCreateForm({...createForm, notes: e.target.value})} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500" placeholder="e.g., Main call center agent" rows={2} /></div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">Cancel</button>
                  <button type="submit" disabled={createLoading} className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg disabled:opacity-50">{createLoading ? "Creating..." : "Create Agent"}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCredentialsModal && newCredentials && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-md">
              <div className="p-4 border-b border-slate-700 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center"><svg className="h-5 w-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></div>
                <div><h3 className="text-lg font-semibold text-white">Credentials Ready</h3><p className="text-slate-400 text-sm">Share these securely with the agent</p></div>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-slate-900 rounded-lg p-4 space-y-3">
                  {newCredentials.name && <div className="flex items-center justify-between"><span className="text-slate-400">Name:</span><span className="text-white font-medium">{newCredentials.name}</span></div>}
                  <div className="flex items-center justify-between"><span className="text-slate-400">Email:</span><div className="flex items-center gap-2"><span className="text-white font-mono text-sm">{newCredentials.email}</span><button onClick={() => copyToClipboard(newCredentials.email)} className="text-slate-400 hover:text-white"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg></button></div></div>
                  <div className="flex items-center justify-between border-t border-slate-700 pt-3"><span className="text-slate-400">PIN (for quick login):</span><div className="flex items-center gap-2"><span className="text-amber-400 font-mono text-xl tracking-widest">{showPin ? newCredentials.pin : "****"}</span><button onClick={() => setShowPin(!showPin)} className="text-slate-400 hover:text-white">{showPin ? <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>}</button><button onClick={() => copyToClipboard(newCredentials.pin)} className="text-slate-400 hover:text-white"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg></button></div></div>
                </div>
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg"><svg className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg><div className="text-sm text-amber-200"><p className="font-medium">Important:</p><ul className="list-disc list-inside text-amber-300/80 mt-1 space-y-1"><li>Share the 4-digit PIN securely (in person or encrypted)</li><li>Agent uses email + PIN for quick portal access</li><li>PIN can be reset anytime from this dashboard</li></ul></div></div>
                <div className="pt-2"><button onClick={() => { setShowCredentialsModal(false); setNewCredentials(null); setShowPin(false); }} className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">Done</button></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistedBookingAgents;
